import {
  CheckBoxFormField,
  FormField,
  Instance,
  Point,
  SignatureFormField,
  TextFormField,
  ToolbarItem,
  WidgetAnnotation,
} from '@nutrient-sdk/viewer'
import {
  EditablePdfButton,
  EditablePdfButtonType,
} from '@urbanx/agx-ui-components'
import NutrientSDK from './NutrientSdk'

const AGX_AUTOMATION_PLACEHOLDER_CUSTOM_DATA_KEY = 'agxAutomationPlaceholder'

const PDF_WIDGET_DIMENSIONS = {
  [EditablePdfButtonType.Text]: { width: 180, height: 40 },
  [EditablePdfButtonType.Signature]: { width: 180, height: 40 },
  [EditablePdfButtonType.Checkbox]: { width: 0, height: 0 },
  [EditablePdfButtonType.SignatureAndDate]: { width: 180, height: 40 },
  [EditablePdfButtonType.Initial]: { width: 40, height: 40 },
}

export type EditablePdfToolbarItem = ToolbarItem & {
  pdfFieldName?: string
}

type EditablePdfWidgetFormField =
  | TextFormField
  | SignatureFormField
  | CheckBoxFormField

function getAutomationPlaceholderCustomData(
  automationFieldName: string,
  isPrimary: boolean,
  automationFormFieldName: string
) {
  return {
    [AGX_AUTOMATION_PLACEHOLDER_CUSTOM_DATA_KEY]: true,
    automationFieldName,
    automationFormFieldName,
    isPrimaryAutomationPlaceholder: isPrimary,
  }
}

function getInvisibleWidgetProperties() {
  return {
    borderColor: NutrientSDK.Color.TRANSPARENT,
    borderWidth: 0,
    backgroundColor: NutrientSDK.Color.TRANSPARENT,
  }
}

function isAutomationPlaceholderCustomData(
  customData: unknown
): customData is Record<string, unknown> {
  if (customData == null || typeof customData !== 'object') return false

  const data = customData as Record<string, unknown>

  return data[AGX_AUTOMATION_PLACEHOLDER_CUSTOM_DATA_KEY] === true
}

export function isAutomationPlaceholderWidget(
  annotation: WidgetAnnotation
): boolean {
  return isAutomationPlaceholderCustomData(annotation.customData)
}

export function getAutomationPlaceholderFieldName(
  customData: unknown
): string | undefined {
  if (!isAutomationPlaceholderCustomData(customData)) return undefined

  return customData.automationFieldName as string | undefined
}

export function getAutomationPlaceholderFormFieldName(
  customData: unknown
): string | undefined {
  if (!isAutomationPlaceholderCustomData(customData)) return undefined

  return customData.automationFormFieldName as string | undefined
}

export function isPrimaryAutomationPlaceholder(customData: unknown): boolean {
  return (
    isAutomationPlaceholderCustomData(customData) &&
    customData.isPrimaryAutomationPlaceholder === true
  )
}

function prepareAutomationPlaceholderWidget<TWidget extends WidgetAnnotation>(
  widget: TWidget
): TWidget {
  return widget
    .set('borderColor', NutrientSDK.Color.TRANSPARENT)
    .set('borderWidth', 0)
    .set('backgroundColor', NutrientSDK.Color.TRANSPARENT) as TWidget
}

function prepareAutomationPlaceholderFormField<
  TFormField extends EditablePdfWidgetFormField,
>(formField: TFormField): TFormField {
  return formField.set('readOnly', true) as TFormField
}

export function getCurrentCenterPosition(instance: Instance): Point {
  // Get current page information from the active page index
  const currPageInfo = instance.pageInfoForIndex(
    instance.viewState.currentPageIndex
  )
  if (!currPageInfo) throw new Error('Current page info not found')

  // Locate the viewport and page elements in the DOM
  const viewportEl =
    instance.contentDocument.querySelector('.PSPDFKit-Viewport')
  const pageEl = instance.contentDocument.querySelector(
    '.PSPDFKit-Viewport .PSPDFKit-Page'
  )

  if (!viewportEl) throw new Error('Viewport element not found')
  if (!pageEl) throw new Error('Page element not found')

  // Get bounding rectangles for position calculations
  const pageRect = pageEl.getBoundingClientRect()
  const viewportRect = viewportEl.getBoundingClientRect()

  // Determine whether to use viewport or page measurements based on which is smaller
  // This ensures we avoid using blank space between the page and viewport for calculations
  const useViewportWidth = viewportEl.clientWidth <= pageRect.width
  const useViewportHeight = viewportEl.clientHeight <= pageRect.height

  // Calculate the center position in client coordinates (browser window)
  const centerClientX = useViewportWidth
    ? viewportRect.left + viewportRect.width / 2
    : pageRect.left + pageRect.width / 2
  const centerClientY = useViewportHeight
    ? viewportRect.top + viewportRect.height / 2
    : pageRect.top + pageRect.height / 2

  // Transform the client coordinates to PDF page space coordinates
  const centerPosition = instance.transformContentClientToPageSpace(
    new NutrientSDK.Geometry.Point({
      x: centerClientX,
      y: centerClientY,
    }),
    instance.viewState.currentPageIndex
  )

  // Normalize coordinates to ensure they're within the page bounds
  return new NutrientSDK.Geometry.Point({
    x: Math.max(0, Math.min(centerPosition.x, currPageInfo.width)),
    y: Math.max(0, Math.min(centerPosition.y, currPageInfo.height)),
  }) as Point
}

export function getSignatureWidget(
  pageIndex: number,
  widgetName: string,
  fieldName: string,
  left: number,
  top: number,
  width: number,
  height: number,
  isPrimary = true
): { widget: WidgetAnnotation; formField: SignatureFormField } {
  const prependedFormField = fieldName.endsWith('SignHere')
    ? fieldName
    : fieldName + 'SignHere'

  const widget = new NutrientSDK.Annotations.WidgetAnnotation({
    pageIndex: pageIndex,
    boundingBox: new NutrientSDK.Geometry.Rect({
      left: left,
      top: top,
      width: width,
      height: height,
    }),
    name: widgetName,
    formFieldName: prependedFormField,
    ...getInvisibleWidgetProperties(),
    customData: getAutomationPlaceholderCustomData(
      fieldName,
      isPrimary,
      prependedFormField
    ),
    id: NutrientSDK.generateInstantId(),
  })

  const formField = new NutrientSDK.FormFields.SignatureFormField({
    name: prependedFormField,
    label: 'Sign Here',
    readOnly: true,
    annotationIds: NutrientSDK.Immutable.List([widget.id]),
  })

  return {
    widget: prepareAutomationPlaceholderWidget(widget),
    formField: prepareAutomationPlaceholderFormField(formField),
  }
}

export function getDateSignedWidget(
  pageIndex: number,
  widgetName: string,
  fieldName: string,
  left: number,
  top: number,
  width: number,
  height: number,
  isPrimary = true
): { widget: WidgetAnnotation; formField: TextFormField } {
  const prependedFormField = fieldName.endsWith('DateSigned')
    ? fieldName
    : fieldName + 'DateSigned'

  const widget = new NutrientSDK.Annotations.WidgetAnnotation({
    pageIndex: pageIndex,
    boundingBox: new NutrientSDK.Geometry.Rect({
      left: left,
      top: top,
      width: width,
      height: height,
    }),
    name: widgetName + ' Date',
    formFieldName: prependedFormField,
    ...getInvisibleWidgetProperties(),
    customData: getAutomationPlaceholderCustomData(
      fieldName,
      isPrimary,
      prependedFormField
    ),
    id: NutrientSDK.generateInstantId(),
  })

  const formField = new NutrientSDK.FormFields.TextFormField({
    name: prependedFormField,
    label: 'TextFormField',
    readOnly: true,
    annotationIds: NutrientSDK.Immutable.List([widget.id]),
  })

  return {
    widget: prepareAutomationPlaceholderWidget(widget),
    formField: prepareAutomationPlaceholderFormField(formField),
  }
}

export function getCheckboxWidget(
  pageIndex: number,
  widgetName: string,
  fieldName: string,
  left: number,
  top: number,
  isPrimary = true
): { widget: WidgetAnnotation; formField: CheckBoxFormField } {
  const widget = new NutrientSDK.Annotations.WidgetAnnotation({
    pageIndex: pageIndex,
    boundingBox: new NutrientSDK.Geometry.Rect({
      left: left,
      top: top,
    }),
    name: widgetName,
    formFieldName: fieldName,
    ...getInvisibleWidgetProperties(),
    customData: getAutomationPlaceholderCustomData(
      fieldName,
      isPrimary,
      fieldName
    ),
    id: NutrientSDK.generateInstantId(),
  })

  const formField = new NutrientSDK.FormFields.CheckBoxFormField({
    name: fieldName,
    readOnly: true,
    annotationIds: NutrientSDK.Immutable.List([widget.id]),
  })

  return {
    widget: prepareAutomationPlaceholderWidget(widget),
    formField: prepareAutomationPlaceholderFormField(formField),
  }
}

export function getTextWidget(
  pageIndex: number,
  widgetName: string,
  fieldName: string,
  left: number,
  top: number,
  width: number,
  height: number,
  isPrimary = true
): { widget: WidgetAnnotation; formField: TextFormField } {
  const widget = new NutrientSDK.Annotations.WidgetAnnotation({
    pageIndex: pageIndex,
    boundingBox: new NutrientSDK.Geometry.Rect({
      left: left,
      top: top,
      width: width,
      height: height,
    }),
    name: widgetName,
    formFieldName: fieldName,
    ...getInvisibleWidgetProperties(),
    customData: getAutomationPlaceholderCustomData(
      fieldName,
      isPrimary,
      fieldName
    ),
    id: NutrientSDK.generateInstantId(),
  })

  const formField = new NutrientSDK.FormFields.TextFormField({
    name: fieldName,
    readOnly: true,
    annotationIds: NutrientSDK.Immutable.List([widget.id]),
  })

  return {
    widget: prepareAutomationPlaceholderWidget(widget),
    formField: prepareAutomationPlaceholderFormField(formField),
  }
}

export function getWidgets(
  button: EditablePdfButton,
  pageIndex: number,
  centerPosition: Point
): {
  widget: WidgetAnnotation
  formField: TextFormField | SignatureFormField | CheckBoxFormField
}[] {
  const { width, height } = PDF_WIDGET_DIMENSIONS[button.pdfFieldType]
  switch (button.pdfFieldType) {
    case EditablePdfButtonType.Text:
      return [
        getTextWidget(
          pageIndex,
          button.buttonName,
          button.pdfFieldName,
          centerPosition.x - width / 2,
          centerPosition.y,
          width,
          height
        ),
      ]
    case EditablePdfButtonType.Signature:
      return [
        getSignatureWidget(
          pageIndex,
          button.buttonName,
          button.pdfFieldName,
          centerPosition.x - width / 2,
          centerPosition.y,
          width,
          height,
          true
        ),
      ]
    case EditablePdfButtonType.Checkbox:
      return [
        getCheckboxWidget(
          pageIndex,
          button.buttonName,
          button.pdfFieldName,
          centerPosition.x,
          centerPosition.y
        ),
      ]
    case EditablePdfButtonType.SignatureAndDate:
      return [
        getSignatureWidget(
          pageIndex,
          button.buttonName,
          button.pdfFieldName,
          centerPosition.x - width - 20,
          centerPosition.y,
          width,
          height,
          true
        ),
        getDateSignedWidget(
          pageIndex,
          button.buttonName,
          button.pdfFieldName,
          centerPosition.x + 20,
          centerPosition.y,
          width,
          height,
          false
        ),
      ]
    case EditablePdfButtonType.Initial:
      return [
        getTextWidget(
          pageIndex,
          button.buttonName,
          button.pdfFieldName,
          centerPosition.x - width / 2,
          centerPosition.y,
          width,
          height
        ),
      ]
    default:
      throw new Error('Invalid widget type')
  }
}

export function setAnnotationBadge(
  annotationId: string,
  annotationName: string
) {
  if (annotationId == null || annotationName == null) return

  const pdfContainer = document
    .querySelectorAll(`.PSPDFKit-Container`)
    .item(0) as HTMLDivElement
  const shadowRoot = pdfContainer?.shadowRoot

  if (shadowRoot != null) {
    const el = document.createElement('style')
    el.innerText = `.PSPDFKit-Annotation[data-annotation-id='${annotationId}']::after {
      content: '${annotationName}';
    }`
    shadowRoot.appendChild(el)
  }
}

export async function prepareAutomationPlaceholdersForExport(
  instance: Instance
): Promise<void> {
  const automationWidgets: WidgetAnnotation[] = []

  for (let pageIndex = 0; pageIndex < instance.totalPageCount; pageIndex++) {
    const annotations = await instance.getAnnotations(pageIndex)

    automationWidgets.push(
      ...Array.from(annotations.values()).filter(
        (annotation): annotation is WidgetAnnotation =>
          annotation instanceof NutrientSDK.Annotations.WidgetAnnotation &&
          isAutomationPlaceholderWidget(annotation)
      )
    )
  }

  if (automationWidgets.length === 0) return

  const updatedWidgets = automationWidgets.map((widget) =>
    prepareAutomationPlaceholderWidget(widget)
  )
  const automationFormFieldNames = [
    ...new Set(
      automationWidgets
        .map((widget) =>
          getAutomationPlaceholderFormFieldName(widget.customData)
        )
        .filter((fieldName): fieldName is string => fieldName != null)
    ),
  ]
  const currentFields = await instance.getFormFields()
  const fieldsToUpdate = Array.from(currentFields.values()).filter((field) =>
    automationFormFieldNames.includes(field.name)
  )
  const updatedFields = fieldsToUpdate.map((field) =>
    field.set('readOnly', true)
  ) as FormField[]

  await instance.update([...updatedWidgets, ...updatedFields])
  await instance.setFormFieldValues(
    automationFormFieldNames.reduce<Record<string, null>>((acc, fieldName) => {
      acc[fieldName] = null
      return acc
    }, {})
  )
}
